<script setup>
import { ref, computed, watch, onMounted, nextTick, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';
import MessageAPI from 'dashboard/api/inbox/message';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const PER_PAGE = 25;
const STEP_COMPLETED_NUMBER_BG = '#10b981';
const store = useStore();
const { t } = useI18n();

const loadingList = ref(false);
const sendingMessage = ref(false);
const conversations = ref([]);
const composerMessage = ref('');

const sectionConversationsOpen = ref(true);
const sectionMessageOpen = ref(false);
const sectionScheduleOpen = ref(false);

const selectedIds = ref(new Set());
const sendNow = ref(true);
const scheduledTime = ref('');

const customAttributes = useMapGetter('attributes/getAttributes');

const variablesDialogRef = useTemplateRef('variablesDialogRef');

const openVariablesModal = () => {
  nextTick(() => {
    variablesDialogRef.value?.open();
  });
};

const standardVariablesList = computed(() =>
  MESSAGE_VARIABLES.map(v => ({
    key: v.key,
    description: v.label,
  }))
);

const customVariablesList = computed(() =>
  (customAttributes.value || []).map(attribute => {
    const model = attribute.attribute_model ?? attribute.attributeModel ?? '';
    const prefix =
      model === 'conversation_attribute' ? 'conversation' : 'contact';
    const attrKey = attribute.attribute_key ?? attribute.attributeKey ?? '';
    const key = `${prefix}.custom_attribute.${attrKey}`;
    const description =
      attribute.attribute_description ?? attribute.attributeDescription ?? key;
    return { key, description };
  })
);

const isProcessing = ref(false);
const processedItems = ref([]);
const successCount = ref(0);
const errorCount = ref(0);
const progress = ref(0);

const plainTextFromHtml = html => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isComposerEmpty = computed(
  () => plainTextFromHtml(composerMessage.value) === ''
);

const conversationTitle = c => {
  const name = c.meta?.sender?.name;
  return name || `#${c.id}`;
};

const hasSelections = computed(
  () => selectedIds.value.size > 0 && conversations.value.length > 0
);

const allListedSelected = computed(
  () =>
    conversations.value.length > 0 &&
    selectedIds.value.size === conversations.value.length
);

const messageBodies = computed(() => {
  if (isComposerEmpty.value) return [];
  return [composerMessage.value];
});

const canSendMessages = computed(
  () =>
    messageBodies.value.length > 0 &&
    hasSelections.value &&
    (sendNow.value || scheduledTime.value.trim() !== '')
);

const step1Done = computed(() => hasSelections.value);
const step2Done = computed(() => messageBodies.value.length > 0);
const step3Done = computed(
  () => sendNow.value || scheduledTime.value.trim() !== ''
);

const highlightedStep = computed(() => {
  if (!step1Done.value) return 1;
  if (!step2Done.value) return 2;
  if (!step3Done.value) return 3;
  return null;
});

const isCompleted = computed(
  () => progress.value === 100 && !sendingMessage.value
);

watch(step1Done, done => {
  if (done) sectionMessageOpen.value = true;
});

watch(step2Done, done => {
  if (done && !sendNow.value) {
    sectionScheduleOpen.value = true;
  }
});

watch(sendNow, now => {
  if (!step2Done.value) return;
  if (now) {
    sectionScheduleOpen.value = false;
  } else {
    sectionScheduleOpen.value = true;
  }
});

const buildListParams = page => ({ page, status: 'open' });

const fetchConversations = async () => {
  loadingList.value = true;
  selectedIds.value = new Set();
  const all = [];
  let page = 1;
  try {
    while (page < 500) {
      const response = await ConversationAPI.get(buildListParams(page));
      const payload = response.data?.data?.payload ?? [];
      all.push(...payload);
      if (payload.length < PER_PAGE) break;
      page += 1;
    }
    conversations.value = all;
  } catch (error) {
    conversations.value = [];
    emitter.emit('newToastMessage', {
      message: error?.message || t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
      type: 'error',
    });
  } finally {
    loadingList.value = false;
  }
};

const toggleId = id => {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
};

const selectAllListed = () => {
  if (allListedSelected.value) {
    selectedIds.value = new Set();
  } else {
    selectedIds.value = new Set(conversations.value.map(c => c.id));
  }
};

const handleSend = async () => {
  if (!canSendMessages.value) return;

  const bodies = messageBodies.value;
  if (bodies.length === 0) return;

  const targets = conversations.value.filter(c => selectedIds.value.has(c.id));

  isProcessing.value = true;
  sendingMessage.value = true;
  processedItems.value = [];
  successCount.value = 0;
  errorCount.value = 0;
  progress.value = 0;

  try {
    for (let i = 0; i < targets.length; i += 1) {
      const c = targets[i];
      try {
        const inboxId = c.inbox_id;
        const messageContent = bodies[i % bodies.length];
        if (sendNow.value) {
          await MessageAPI.create({
            conversationId: c.id,
            message: messageContent,
            private: false,
            message_type: 'outgoing',
            inbox_id: inboxId,
          });
        } else {
          const plain = plainTextFromHtml(messageContent);
          const title =
            plain.length > 50 ? `${plain.slice(0, 50)}…` : plain || '…';
          await ConversationAPI.scheduleMessage({
            conversation_id: c.id,
            inbox_id: inboxId,
            content: messageContent,
            scheduled_at: new Date(scheduledTime.value).toISOString(),
            title,
          });
        }
        processedItems.value.push({
          id: c.id,
          title: conversationTitle(c),
          conversationId: c.id,
          status: 'success',
        });
        successCount.value += 1;
      } catch (error) {
        processedItems.value.push({
          id: c.id,
          title: conversationTitle(c),
          conversationId: c.id,
          status: 'error',
          error: error?.message,
        });
        errorCount.value += 1;
      }
      progress.value = ((i + 1) / targets.length) * 100;
    }

    emitter.emit('newToastMessage', {
      message: sendNow.value
        ? t('MESSAGE_DISPATCHER.TOAST_SEND_DONE', {
            ok: successCount.value,
            fail: errorCount.value,
          })
        : t('MESSAGE_DISPATCHER.TOAST_SCHEDULE_DONE', {
            ok: successCount.value,
            fail: errorCount.value,
          }),
      type: successCount.value > 0 ? 'success' : 'error',
    });
  } catch {
    emitter.emit('newToastMessage', {
      message: t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
      type: 'error',
    });
  } finally {
    sendingMessage.value = false;
  }
};

const finishProgress = () => {
  isProcessing.value = false;
  processedItems.value = [];
  successCount.value = 0;
  errorCount.value = 0;
  progress.value = 0;
};

onMounted(() => {
  store.dispatch('attributes/get');
  fetchConversations();
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full min-h-0 overflow-auto bg-n-surface-1">
    <div class="max-w-5xl mx-auto w-full px-4 py-6 md:px-8 md:py-8 space-y-6">
      <header class="space-y-1">
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('MESSAGE_DISPATCHER.DESCRIPTION') }}
        </p>
      </header>

      <div v-if="isProcessing" class="space-y-6">
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-sm"
        >
          <div class="text-center space-y-2">
            <p
              class="text-xs font-semibold uppercase tracking-wide"
              :class="isCompleted ? 'text-emerald-600' : 'text-n-brand'"
            >
              {{
                isCompleted
                  ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.COMPLETED_BADGE')
                  : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_BADGE')
              }}
            </p>
            <h3
              class="text-xl font-semibold text-n-slate-12"
              :class="isCompleted ? 'text-emerald-600' : ''"
            >
              {{
                isCompleted
                  ? t('MESSAGE_DISPATCHER.DONE_TITLE')
                  : t('MESSAGE_DISPATCHER.PROGRESS_TITLE')
              }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{
                t('MESSAGE_DISPATCHER.PROGRESS_HINT', {
                  count: processedItems.length,
                  total: selectedIds.size,
                })
              }}
            </p>
          </div>
          <div class="relative mt-6 space-y-3">
            <div
              class="flex items-center justify-between text-xs font-semibold"
            >
              <span
                class="inline-flex items-center rounded-full px-3 py-1"
                :class="
                  isCompleted
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-n-alpha-2 text-n-brand'
                "
              >
                {{ Math.round(progress) }}%
              </span>
              <span class="text-n-slate-11">
                {{ successCount }}
                {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.SUCCESS') }} ·
                {{ errorCount }}
                {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.ERROR') }}
              </span>
            </div>
            <div class="h-3 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="isCompleted ? 'bg-emerald-500' : 'bg-n-brand'"
                :style="{ width: `${progress}%` }"
              />
            </div>
          </div>
        </div>

        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm overflow-hidden"
        >
          <div
            class="flex items-center justify-between border-b border-n-weak px-6 py-4 text-sm"
          >
            <div class="font-medium text-n-slate-12">
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE') }}
            </div>
            <span class="text-xs text-n-slate-11">
              {{
                t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_RESULTS', {
                  count: processedItems.length,
                })
              }}
            </span>
          </div>
          <div class="max-h-[430px] divide-y divide-n-weak overflow-y-auto">
            <div
              v-for="item in processedItems"
              :key="item.id"
              class="flex items-center gap-3 px-6 py-4 text-sm hover:bg-n-alpha-1"
            >
              <span
                class="flex h-10 w-10 items-center justify-center rounded-full"
                :class="
                  item.status === 'success'
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40'
                    : 'bg-ruby-100 text-ruby-600 dark:bg-ruby-900/40'
                "
              >
                <fluent-icon
                  :icon="
                    item.status === 'success'
                      ? 'checkmark-circle'
                      : 'error-circle'
                  "
                  size="18"
                />
              </span>
              <div class="flex-1 min-w-0">
                <p class="font-medium text-n-slate-12 truncate">
                  {{ item.title }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                      id: item.conversationId,
                    })
                  }}
                </p>
                <p v-if="item.error" class="mt-2 text-xs text-ruby-600">
                  {{ item.error }}
                </p>
              </div>
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold shrink-0"
                :class="
                  item.status === 'success'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-ruby-100 text-ruby-700 dark:bg-ruby-900/40 dark:text-ruby-300'
                "
              >
                {{
                  item.status === 'success'
                    ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_SUCCESS')
                    : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_ERROR')
                }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="finishProgress"
          >
            {{ t('MESSAGE_DISPATCHER.CLOSE') }}
          </Button>
        </div>
      </div>

      <div v-else class="space-y-6">
        <div
          class="space-y-4 rounded-2xl border bg-n-solid-1 p-6 shadow-sm transition-shadow"
          :class="
            highlightedStep === 1
              ? 'border-n-brand ring-2 ring-n-brand'
              : 'border-n-weak'
          "
        >
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold text-white"
                :class="{ 'bg-n-brand': !step1Done }"
                :style="
                  step1Done ? { backgroundColor: STEP_COMPLETED_NUMBER_BG } : {}
                "
              >
                <fluent-icon v-if="step1Done" icon="checkmark" size="14" />
                <template v-else>1</template>
              </span>
              <p class="mb-0 text-xs uppercase tracking-wide text-n-slate-11">
                {{ t('MESSAGE_DISPATCHER.CONVERSATIONS_SECTION') }}
              </p>
              <h4 class="text-base font-semibold text-n-slate-12 truncate">
                {{ t('MESSAGE_DISPATCHER.SELECT_CONVERSATIONS') }}
              </h4>
              <span
                class="inline-flex items-center rounded-full border border-n-weak bg-n-alpha-1 px-1.5 py-0.5 text-[10px] font-semibold text-n-slate-11 shrink-0"
              >
                {{ selectedIds.size }}/{{ conversations.length }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                color="slate"
                size="sm"
                :is-loading="loadingList"
                @click="fetchConversations"
              >
                {{ t('MESSAGE_DISPATCHER.REFETCH') }}
              </Button>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                @click="selectAllListed"
              >
                {{
                  allListedSelected
                    ? t('KANBAN.BULK_ACTIONS.CLEAR_SELECTION')
                    : t('KANBAN.BULK_ACTIONS.SELECT_ALL')
                }}
              </Button>
              <button
                type="button"
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-n-weak text-n-slate-11 transition hover:bg-n-alpha-1"
                :aria-expanded="sectionConversationsOpen"
                @click="sectionConversationsOpen = !sectionConversationsOpen"
              >
                <fluent-icon
                  :icon="
                    sectionConversationsOpen ? 'chevron-up' : 'chevron-down'
                  "
                  size="16"
                />
              </button>
            </div>
          </div>

          <div v-show="sectionConversationsOpen" class="space-y-4">
            <div v-if="loadingList" class="flex justify-center py-8">
              <span class="loading-spinner" />
            </div>
            <div
              v-else-if="conversations.length > 0"
              class="max-h-64 space-y-2 overflow-y-auto"
            >
              <div
                v-for="c in conversations"
                :key="c.id"
                class="flex cursor-pointer items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-1 px-4 py-2 text-sm shadow-sm transition hover:border-n-brand hover:bg-n-alpha-1"
                @click="toggleId(c.id)"
              >
                <input
                  type="checkbox"
                  :checked="selectedIds.has(c.id)"
                  class="h-4 w-4 rounded border-n-weak text-n-brand focus:ring-n-brand bg-n-solid-1"
                  @click.stop="toggleId(c.id)"
                />
                <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p class="font-medium text-n-slate-12 truncate">
                    {{ conversationTitle(c) }}
                  </p>
                  <p class="text-xs text-n-slate-11">
                    {{
                      t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                        id: c.id,
                      })
                    }}
                    <span v-if="c.inbox_id" class="text-n-slate-10">
                      · inbox #{{ c.inbox_id }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div
              v-else
              class="rounded-xl border border-dashed border-n-weak bg-n-alpha-1 py-6 text-center text-sm text-n-slate-11"
            >
              {{ t('MESSAGE_DISPATCHER.EMPTY_CONVERSATIONS') }}
            </div>
          </div>
        </div>

        <div
          class="space-y-4 rounded-2xl border bg-n-solid-1 p-6 shadow-sm transition-shadow"
          :class="
            highlightedStep === 2
              ? 'border-n-brand ring-2 ring-n-brand'
              : 'border-n-weak'
          "
        >
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0 flex flex-1 items-start gap-2">
              <span
                class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                :class="{ 'bg-n-brand': !step2Done }"
                :style="
                  step2Done ? { backgroundColor: STEP_COMPLETED_NUMBER_BG } : {}
                "
              >
                <fluent-icon v-if="step2Done" icon="checkmark" size="14" />
                <template v-else>2</template>
              </span>
              <div class="min-w-0">
                <p class="text-xs uppercase tracking-wide text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.MESSAGE_SECTION') }}
                </p>
                <h4 class="text-lg font-semibold text-n-slate-12">
                  {{ t('MESSAGE_DISPATCHER.COMPOSER_TITLE') }}
                </h4>
                <p class="text-sm text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.COMPOSER_DESCRIPTION') }}
                </p>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-braces"
                @click="openVariablesModal"
              >
                {{ t('MESSAGE_DISPATCHER.VARIABLES_BUTTON') }}
              </Button>
              <button
                type="button"
                class="flex h-9 w-9 items-center justify-center rounded-lg border border-n-weak text-n-slate-11 transition hover:bg-n-alpha-1"
                @click="sectionMessageOpen = !sectionMessageOpen"
              >
                <fluent-icon
                  :icon="sectionMessageOpen ? 'chevron-up' : 'chevron-down'"
                  size="16"
                />
              </button>
            </div>
          </div>

          <div v-show="sectionMessageOpen" class="space-y-4">
            <div class="editor-wrap w-full">
              <WootMessageEditor
                id="message-dispatcher-composer"
                v-model="composerMessage"
                class="message-editor min-h-[12rem] [&>div]:px-1"
                channel-type="Context::Default"
                enable-variables
                :focus-on-mount="false"
                :placeholder="t('MESSAGE_DISPATCHER.COMPOSER_PLACEHOLDER')"
              />
            </div>
          </div>
        </div>

        <div
          v-if="!sendNow"
          class="space-y-4 rounded-2xl border bg-n-solid-1 p-6 shadow-sm transition-shadow"
          :class="
            highlightedStep === 3
              ? 'border-n-brand ring-2 ring-n-brand'
              : 'border-n-weak'
          "
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex flex-1 items-start gap-2">
              <span
                class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                :class="{ 'bg-n-brand': !step3Done }"
                :style="
                  step3Done ? { backgroundColor: STEP_COMPLETED_NUMBER_BG } : {}
                "
              >
                <fluent-icon v-if="step3Done" icon="checkmark" size="14" />
                <template v-else>3</template>
              </span>
              <div class="min-w-0">
                <p class="text-xs uppercase tracking-wide text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.SCHEDULE_SECTION') }}
                </p>
                <h4 class="text-lg font-semibold text-n-slate-12">
                  {{ t('MESSAGE_DISPATCHER.SCHEDULE_DATETIME') }}
                </h4>
                <p class="text-sm text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.SCHEDULE_HELP') }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-n-weak text-n-slate-11 transition hover:bg-n-alpha-1"
              @click="sectionScheduleOpen = !sectionScheduleOpen"
            >
              <fluent-icon
                :icon="sectionScheduleOpen ? 'chevron-up' : 'chevron-down'"
                size="16"
              />
            </button>
          </div>
          <div v-show="sectionScheduleOpen">
            <label class="mb-2 block text-sm font-semibold text-n-slate-12">
              {{ t('MESSAGE_DISPATCHER.SCHEDULE_DATETIME') }}
            </label>
            <input
              v-model="scheduledTime"
              type="datetime-local"
              class="w-full rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12 p-3 text-sm"
            />
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-end gap-4 pb-8">
          <div class="flex items-center gap-3 mr-auto">
            <span
              class="text-sm font-medium text-n-slate-12"
              :class="{ 'text-n-slate-10': sendNow }"
            >
              {{ t('MESSAGE_DISPATCHER.TOGGLE_SCHEDULE_LATER') }}
            </span>
            <Switch v-model="sendNow" />
            <span
              class="text-sm font-medium text-n-slate-12"
              :class="{ 'text-n-slate-10': !sendNow }"
            >
              {{ t('MESSAGE_DISPATCHER.TOGGLE_SCHEDULE_NOW') }}
            </span>
          </div>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :is-loading="sendingMessage"
            :disabled="!canSendMessages"
            @click="handleSend"
          >
            {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGES') }}
          </Button>
        </div>
      </div>
    </div>

    <Dialog
      ref="variablesDialogRef"
      width="2xl"
      overflow-y-auto
      :title="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.TITLE')"
      :description="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.INTRO')"
      :show-confirm-button="false"
      :cancel-button-label="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.CLOSE')"
    >
      <div
        class="flex flex-col gap-6 max-h-[min(70vh,28rem)] overflow-y-auto pe-1"
      >
        <div class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-0">
            {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.STANDARD_SECTION') }}
          </h4>
          <ul
            class="m-0 list-none divide-y divide-n-weak rounded-xl border border-n-weak bg-n-solid-1"
          >
            <li
              v-for="row in standardVariablesList"
              :key="row.key"
              class="px-4 py-3 flex flex-col gap-1"
            >
              <code class="text-sm font-mono text-n-brand break-all">
                {{ row.key }}
              </code>
              <span class="text-sm text-n-slate-11">{{ row.description }}</span>
            </li>
          </ul>
        </div>

        <div v-if="customVariablesList.length > 0" class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-0">
            {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.CUSTOM_SECTION') }}
          </h4>
          <ul
            class="m-0 list-none divide-y divide-n-weak rounded-xl border border-n-weak bg-n-solid-1"
          >
            <li
              v-for="row in customVariablesList"
              :key="row.key"
              class="px-4 py-3 flex flex-col gap-1"
            >
              <code class="text-sm font-mono text-n-brand break-all">
                {{ row.key }}
              </code>
              <span class="text-sm text-n-slate-11">{{ row.description }}</span>
            </li>
          </ul>
        </div>

        <p class="text-xs text-n-slate-11 mb-0">
          {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.EDITOR_HINT') }}
        </p>
      </div>
    </Dialog>
  </div>
</template>
